import React from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, FileText, FileSignature, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLang, translateFr as tfr } from "@/i18n";

const KIND_ICON = { contract: FileSignature, contract_deadline: CalendarClock, document: FileText };

const URGENCY = {
  critical: { label: "Critique", cls: "text-danger bg-danger/10 border-danger/25" },
  soon: { label: tfr("Bientôt"), cls: "text-warning bg-warning/10 border-warning/25" },
  upcoming: { label: tfr("À venir"), cls: "text-muted-foreground bg-secondary border-border" },
};

function daysLabel(d, tf) {
  if (d === null || d === undefined) return "—";
  if (d < 0) return `${tf("En retard de")} ${-d} j`;
  if (d === 0) return tf("Aujourd'hui");
  if (d === 1) return tf("Demain");
  return `${tf("Dans")} ${d} ${tf("jours")}`;
}

export default function UpcomingDeadlinesCard({ deadlines }) {
  const { tf } = useLang();
  const navigate = useNavigate();
  const items = deadlines?.items || [];
  return (
    <div
      className="rounded-2xl border border-border bg-card p-4 md:p-5 flex flex-col min-w-0 lg:min-h-[520px] xl:min-h-0 xl:h-[clamp(470px,31vw,570px)]"
      data-testid="upcoming-deadlines-card"
    >
      <div className="shrink-0 flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] font-semibold text-muted-foreground">
        <CalendarClock className="h-3.5 w-3.5" /> {tf("Échéances à venir")}
      </div>
      {deadlines === null ? (
        <div className="flex-1 mt-3 space-y-2">
          {[0, 1, 2].map((i) => <div key={i} className="h-12 rounded-xl bg-secondary/60 animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground py-8" data-testid="deadlines-empty">
          {tf("Aucune échéance à venir.")}
        </div>
      ) : (
        <ul className="flex-1 min-h-0 mt-3 space-y-2 overflow-y-auto overflow-x-hidden scroll-thin pb-1 pr-1">
          {items.map((it) => {
            const Icon = KIND_ICON[it.kind] || CalendarClock;
            const u = URGENCY[it.urgency] || URGENCY.upcoming;
            return (
              <li key={it.key}>
                <button
                  onClick={() => navigate(it.route)}
                  className="w-full flex items-center gap-3 rounded-xl border border-border bg-white/[0.01] hover:bg-secondary/50 px-3 py-2.5 text-left transition-colors min-w-0"
                  data-testid={`deadline-item-${it.key}`}
                >
                  <span className={cn("h-8 w-8 rounded-lg border flex items-center justify-center shrink-0", u.cls)}>
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium truncate">{it.title}</span>
                    <span className="block text-[11px] text-muted-foreground truncate">{tf(it.subtitle)}</span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span className={cn("block text-[11px] font-semibold",
                      it.urgency === "critical" ? "text-danger" : it.urgency === "soon" ? "text-warning" : "text-muted-foreground")}>
                      {daysLabel(it.days_left, tf)}
                    </span>
                    <span className={cn("inline-block mt-0.5 text-[9.5px] uppercase tracking-wide rounded-full border px-1.5 py-px", u.cls)}>
                      {tf(u.label)}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Compact contextual alert shown only when at least one genuinely critical deadline exists. */
export function CriticalAlertBanner({ deadlines }) {
  const { tf } = useLang();
  const navigate = useNavigate();
  const critical = deadlines?.critical || [];
  if (!critical.length) return null;
  const top = critical[0];
  return (
    <div className="rounded-xl border border-danger/30 bg-danger/[0.06] px-4 py-3 flex items-center gap-3 flex-wrap"
      data-testid="critical-alert-banner" role="alert">
      <AlertTriangle className="h-4 w-4 text-danger shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold truncate">
          {top.title} · {tf(top.subtitle)}
        </div>
        <div className="text-xs text-muted-foreground">
          {daysLabel(top.days_left, tf)}
          {critical.length > 1 && ` · ${critical.length - 1} ${tf("autre(s) échéance(s) critique(s)")}`}
        </div>
      </div>
      <button onClick={() => navigate(top.route)}
        className="shrink-0 rounded-lg border border-danger/30 text-danger hover:bg-danger/10 text-xs font-medium px-3 py-1.5 transition-colors"
        data-testid="critical-alert-action">
        {tf(top.action_label || "Ouvrir")}
      </button>
    </div>
  );
}
